/**
 * Quiz runner.
 *
 * The browser only collects the selected choice ids and posts them. Scoring,
 * pass/fail and the corrections all come back from the server: nothing here can
 * change a result, and the right answers are unknown until submission.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  History,
  ListChecks,
  RotateCcw,
  Send,
  Timer,
  X,
  XCircle,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { MarkdownView } from '@/components/content/MarkdownView';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ProgressBar, ProgressRing } from '@/components/ui/Progress';
import { FullPageSpinner, Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';
import { api, ApiError } from '@/services/api';
import { queryKeys } from '@/services/queries';
import type { QuizAttemptStart, QuizAttemptSummary, QuizResult } from '@/types/api';

type Selections = Record<string, string[]>;

export function QuizPage() {
  const { quizId = '' } = useParams();
  const queryClient = useQueryClient();
  const [attempt, setAttempt] = useState<QuizAttemptStart | null>(null);
  const [selections, setSelections] = useState<Selections>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [current, setCurrent] = useState(0);
  const [startError, setStartError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);

  const history = useQuery({
    queryKey: queryKeys.quizAttempts(quizId),
    queryFn: () => api.get<QuizAttemptSummary[]>(`/quizzes/${quizId}/attempts`),
    enabled: Boolean(quizId),
  });

  const start = useMutation({
    mutationFn: () => api.post<QuizAttemptStart>(`/quizzes/${quizId}/attempts`),
    onSuccess: (data) => {
      setAttempt(data);
      setSelections({});
      setResult(null);
      setCurrent(0);
      setStartError(null);
    },
    onError: (error) => {
      setStartError(
        error instanceof ApiError ? error.message : 'Impossible de démarrer la tentative.',
      );
    },
  });

  const submit = useMutation({
    mutationFn: (payload: { answers: { question_id: string; choice_ids: string[] }[] }) =>
      api.post<QuizResult>(`/quizzes/${quizId}/attempts/${attempt?.attempt_id}/submit`, payload),
    onSuccess: (data) => {
      setResult(data);
      setAttempt(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.quizAttempts(quizId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      void queryClient.invalidateQueries({ queryKey: queryKeys.progress });
    },
  });

  // Countdown when the quiz is timed. The server also enforces the deadline, so
  // this timer is a courtesy, not the control.
  useEffect(() => {
    if (!attempt?.deadline_at) {
      setRemaining(null);
      return;
    }
    const deadline = new Date(attempt.deadline_at).getTime();
    const tick = () => setRemaining(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [attempt?.deadline_at]);

  const questions = useMemo(() => attempt?.questions ?? [], [attempt]);
  const answeredCount = useMemo(
    () => questions.filter((question) => (selections[question.id] ?? []).length > 0).length,
    [questions, selections],
  );

  const toggleChoice = (questionId: string, choiceId: string, multiple: boolean) => {
    setSelections((previous) => {
      const existing = previous[questionId] ?? [];
      if (multiple) {
        return {
          ...previous,
          [questionId]: existing.includes(choiceId)
            ? existing.filter((id) => id !== choiceId)
            : [...existing, choiceId],
        };
      }
      return { ...previous, [questionId]: existing[0] === choiceId ? [] : [choiceId] };
    });
  };

  const handleSubmit = () => {
    submit.mutate({
      answers: questions.map((question) => ({
        question_id: question.id,
        choice_ids: selections[question.id] ?? [],
      })),
    });
  };

  if (history.isLoading) return <FullPageSpinner label="Chargement du quiz…" />;

  // ------------------------------------------------------------------ Result view
  if (result) {
    const correctCount = result.corrections.filter((item) => item.is_correct).length;
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <Link
          to="/app"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" />
          Tableau de bord
        </Link>

        <Card className={cn('overflow-hidden', result.passed ? 'border-ok/40' : 'border-warn/40')}>
          <div
            className={cn(
              'flex flex-wrap items-center gap-6 px-6 py-7',
              result.passed ? 'bg-ok/[0.07]' : 'bg-warn/[0.07]',
            )}
          >
            <ProgressRing value={result.score_percent} label="score" size={104} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {result.passed ? (
                  <CheckCircle2 className="h-5 w-5 text-ok" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-warn" />
                )}
                <h1 className="text-xl font-bold">
                  {result.passed ? 'Quiz réussi' : 'Seuil de réussite non atteint'}
                </h1>
              </div>
              <p className="mt-2 text-sm text-muted">
                {correctCount}/{result.corrections.length} bonne
                {correctCount > 1 ? 's' : ''} réponse{correctCount > 1 ? 's' : ''} —{' '}
                {result.score_points}/{result.max_points} points. Seuil requis :{' '}
                {result.pass_score_percent} %.
              </p>
              <p className="mt-1 text-2xs text-faint">
                Tentative {result.attempt_number} · soumise le {formatDateTime(result.submitted_at)} ·
                score calculé par le serveur.
              </p>
            </div>
          </div>
        </Card>

        <section aria-labelledby="correction">
          <h2 id="correction" className="text-lg font-bold tracking-tight">
            Correction pédagogique
          </h2>
          <ul className="mt-4 space-y-4">
            {result.corrections.map((correction, index) => (
              <li key={correction.question_id}>
                <Card
                  className={cn(
                    'overflow-hidden',
                    correction.is_correct ? 'border-ok/30' : 'border-danger/30',
                  )}
                >
                  <CardBody>
                  <div className="flex items-start gap-3">
                    <span className="shrink-0">
                      {correction.is_correct ? (
                        <CheckCircle2 className="h-5 w-5 text-ok" />
                      ) : (
                        <XCircle className="h-5 w-5 text-danger" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-2xs font-semibold uppercase tracking-wide text-faint">
                        Question {index + 1} · {correction.points_awarded}/{correction.points_total}{' '}
                        point{correction.points_total > 1 ? 's' : ''}
                      </p>
                      <h3 className="mt-1.5 text-sm font-semibold text-fg">{correction.prompt}</h3>

                      {correction.explanation ? (
                        <div className="mt-3 rounded-lg border border-border bg-surface-2/50 px-4 py-3">
                          <p className="text-2xs font-semibold uppercase tracking-wide text-tech">
                            Explication
                          </p>
                          <div className="mt-1.5 text-sm text-fg/90">
                            <MarkdownView source={correction.explanation} />
                          </div>
                        </div>
                      ) : null}

                      {correction.reference ? (
                        <p className="mt-2.5 flex items-center gap-1.5 text-2xs text-faint">
                          <BookOpen className="h-3 w-3" />
                          {correction.reference}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  </CardBody>
                </Card>
              </li>
            ))}
          </ul>
        </section>

        <div className="flex flex-wrap gap-3">
          <Button onClick={() => start.mutate()} disabled={start.isPending}>
            <RotateCcw className="h-4 w-4" />
            Nouvelle tentative
          </Button>
          <ButtonLink to="/app" variant="outline">
            Retour au tableau de bord
          </ButtonLink>
        </div>
        {startError ? (
          <Alert tone="warning" title="Nouvelle tentative impossible">
            {startError}
          </Alert>
        ) : null}
      </div>
    );
  }

  // ------------------------------------------------------------------ Running view
  if (attempt) {
    const question = questions[current];
    if (!question) return <FullPageSpinner />;
    const multiple = question.kind === 'multiple_choice';
    const selected = selections[question.id] ?? [];

    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Badge tone="ai">
            Tentative {attempt.attempt_number} · question {current + 1}/{questions.length}
          </Badge>
          {remaining !== null ? (
            <Badge tone={remaining < 60 ? 'danger' : 'neutral'}>
              <Timer className="h-3 w-3" />
              {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')}
            </Badge>
          ) : null}
        </div>

        <ProgressBar
          value={(answeredCount / Math.max(questions.length, 1)) * 100}
          label={`${answeredCount}/${questions.length} question${questions.length > 1 ? 's' : ''} répondue${answeredCount > 1 ? 's' : ''}`}
          tone="ai"
        />

        <Card>
          <CardBody className="p-6">
            <p className="text-2xs font-semibold uppercase tracking-wide text-faint">
              {multiple
                ? 'Plusieurs réponses attendues'
                : question.kind === 'true_false'
                  ? 'Vrai ou faux'
                  : 'Une seule réponse'}{' '}
              · {question.points} point{question.points > 1 ? 's' : ''}
            </p>
            <h1 className="mt-3 text-lg font-semibold leading-snug text-fg">{question.prompt}</h1>

            <fieldset className="mt-6 space-y-2.5">
              <legend className="sr-only">{question.prompt}</legend>
              {question.choices.map((choice) => {
                const checked = selected.includes(choice.id);
                return (
                  <label
                    key={choice.id}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-lg border p-3.5 transition-colors',
                      checked
                        ? 'border-tech bg-tech/[0.08]'
                        : 'border-border bg-surface-2/40 hover:border-faint',
                    )}
                  >
                    <input
                      type={multiple ? 'checkbox' : 'radio'}
                      name={question.id}
                      value={choice.id}
                      checked={checked}
                      onChange={() => toggleChoice(question.id, choice.id, multiple)}
                      className="sr-only"
                    />
                    <span
                      className={cn(
                        'mt-0.5 grid h-5 w-5 shrink-0 place-items-center border-2 transition-colors',
                        multiple ? 'rounded' : 'rounded-full',
                        checked ? 'border-tech bg-tech' : 'border-border',
                      )}
                      aria-hidden="true"
                    >
                      {checked ? <Check className="h-3 w-3 text-white" /> : null}
                    </span>
                    <span className="min-w-0 text-sm text-fg">{choice.label}</span>
                  </label>
                );
              })}
            </fieldset>
          </CardBody>
        </Card>

        {/* Question navigator */}
        <div className="flex flex-wrap gap-1.5">
          {questions.map((item, index) => {
            const answered = (selections[item.id] ?? []).length > 0;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setCurrent(index)}
                aria-label={`Aller à la question ${index + 1}${answered ? ' (répondue)' : ''}`}
                aria-current={index === current ? 'true' : undefined}
                className={cn(
                  'h-8 w-8 rounded-md border text-xs font-semibold tabular-nums transition-colors',
                  index === current
                    ? 'border-tech bg-tech text-white'
                    : answered
                      ? 'border-ok/40 bg-ok/10 text-ok'
                      : 'border-border bg-surface-2 text-muted hover:border-faint',
                )}
              >
                {index + 1}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => setCurrent((index) => Math.max(0, index - 1))}
            disabled={current === 0}
          >
            <ArrowLeft className="h-4 w-4" />
            Précédent
          </Button>

          {current < questions.length - 1 ? (
            <Button onClick={() => setCurrent((index) => index + 1)}>
              Suivant
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submit.isPending}>
              {submit.isPending ? <Spinner /> : <Send className="h-4 w-4" />}
              Valider mes réponses
            </Button>
          )}
        </div>

        {answeredCount < questions.length ? (
          <Alert tone="warning">
            {questions.length - answeredCount} question
            {questions.length - answeredCount > 1 ? 's' : ''} sans réponse. Une question non
            répondue est comptée comme fausse.
          </Alert>
        ) : null}

        {submit.isError ? (
          <Alert tone="danger" title="Envoi impossible">
            {submit.error instanceof ApiError
              ? submit.error.message
              : 'La tentative n’a pas pu être soumise.'}
          </Alert>
        ) : null}
      </div>
    );
  }

  // ------------------------------------------------------------------ Intro view
  const attempts = history.data ?? [];
  const best = attempts.reduce<number | null>(
    (max, item) =>
      item.score_percent !== null && item.score_percent !== undefined
        ? Math.max(max ?? 0, item.score_percent)
        : max,
    null,
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/app"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-fg"
      >
        <ArrowLeft className="h-4 w-4" />
        Tableau de bord
      </Link>

      <Card>
        <CardBody className="p-6">
          <Badge tone="ai">
            <ListChecks className="h-3 w-3" />
            Évaluation
          </Badge>
          <h1 className="mt-4 text-2xl font-bold tracking-tight">Quiz du module</h1>
          <p className="mt-2 text-sm text-muted">
            Les réponses correctes ne sont pas transmises au navigateur. Votre score et la
            correction pédagogique sont calculés par le serveur après validation.
          </p>

          {best !== null ? (
            <p className="mt-4 text-sm">
              <span className="text-muted">Meilleur score : </span>
              <span className="font-bold text-fg">{Math.round(best)} %</span>
            </p>
          ) : null}

          {startError ? (
            <Alert tone="warning" title="Tentative impossible" className="mt-5">
              {startError}
            </Alert>
          ) : null}

          <Button onClick={() => start.mutate()} disabled={start.isPending} size="lg" className="mt-6">
            {start.isPending ? <Spinner /> : <ListChecks className="h-4 w-4" />}
            {attempts.length > 0 ? 'Nouvelle tentative' : 'Démarrer le quiz'}
          </Button>
        </CardBody>
      </Card>

      {attempts.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle as="h2" className="flex items-center gap-2">
              <History className="h-4 w-4 text-muted" />
              Historique des tentatives
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <ul className="divide-y divide-border">
              {attempts.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div>
                    <p className="text-sm font-medium text-fg">Tentative {item.attempt_number}</p>
                    <p className="text-2xs text-faint">
                      {item.submitted_at
                        ? `Soumise le ${formatDateTime(item.submitted_at)}`
                        : 'En cours'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {item.score_percent !== null && item.score_percent !== undefined ? (
                      <span className="text-sm font-bold tabular-nums text-fg">
                        {Math.round(item.score_percent)} %
                      </span>
                    ) : null}
                    {item.passed === true ? (
                      <Badge tone="ok">
                        <Check className="h-3 w-3" />
                        Réussi
                      </Badge>
                    ) : item.passed === false ? (
                      <Badge tone="danger">
                        <X className="h-3 w-3" />
                        Échoué
                      </Badge>
                    ) : (
                      <Badge tone="neutral">En cours</Badge>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
